import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";

export function OrderDetailsViewModel(orderId, logout, addSnackbar) {
  const [customers, setCustomers] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState({ data: [] });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const getOrderDetails = async function () {
    setLoading(true);
    let callResult = await makePostApiCall(`fetchOrder/${orderId}`);
    setLoading(false);
    if (callResult.success) {
      setOrder(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getCustomersForDropdown = async function () {
    let callResult = await makePostApiCall(`getCustomersForDropdown`);
    if (callResult.success) {
      setCustomers(callResult.data);
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getMyProducts = async function (search, page) {
    const payload = {
      page: page,
      search: search,
    };
    let callResult = await makePostApiCall(`fetchProductsForDropdown`, payload);
    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const resolveProductPrice = async function (customerId, productId) {
    const callResult = await makePostApiCall(`resolveProductPrice`, {
      customer_id: customerId,
      product_id: productId,
    });
    if (callResult.success) return callResult.data;
    if (callResult.errorStatus === 401) logout();
    showErrorModal(
      callResult.data?.message ?? "Unable to resolve product price.",
    );
    return null;
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const updateOrderApiCall = async function (newOrder) {
    setSaving(true);
    const payload = {
      ...newOrder,
      status:
        typeof newOrder.status === "object"
          ? newOrder.status?.value
          : newOrder.status,
    };
    let callResult = await makePostApiCall(`updateOrder/${orderId}`, payload);
    setSaving(false);
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      await getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onUpdateOrderItem = async function (id, newQuantity) {
    let callResult = await makePostApiCall(`updateOrderItem/${id}`, {
      quantity: newQuantity,
    });
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      await getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const onUpdateOrderItemUnitPrice = async function (id, unitPrice) {
    const callResult = await makePostApiCall(`orderItems/${id}/unitPrice`, {
      unit_price: unitPrice,
    });
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      await getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal(
        callResult.data?.message ?? "Unable to update unit price.",
      );
    }
    return callResult;
  };

  const deleteOrderItemApiCall = async function (id) {
    let callResult = await makePostApiCall(`deleteOrderItem/${id}`);

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      await getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addProductToOrderApiCall = async function (orderId, payload) {
    let callResult = await makePostApiCall(
      `addProductToOrder/${orderId}`,
      payload,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Product added!",
          SnackbarType.Success,
        ),
      );
      await getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const createShipmentFromOrder = async function () {
    setSaving(true);
    let callResult = await makePostApiCall(`orders/${orderId}/shipments`, {
      created_by: order?.created_by,
      notes: null,
    });
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Shipment created successfully.",
          SnackbarType.Success,
        ),
      );
      const shippingId = callResult.data.id ?? callResult.data?.shipping?.id;
      if (shippingId) {
        window.location = `/shippings/${shippingId}`;
        return callResult;
      }
      await getOrderDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    setSaving(false);
    return callResult;
  };

  const printOrder = async function () {
    let callResult = await makePostApiCall(`printOrder/${orderId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  return {
    customers,
    getCustomersForDropdown,
    getMyProducts,
    resolveProductPrice,
    loading,
    products,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateOrderApiCall,
    order,
    getOrderDetails,
    onUpdateOrderItem,
    onUpdateOrderItemUnitPrice,
    deleteOrderItemApiCall,
    addProductToOrderApiCall,
    employeeOptions,
    getUsersForDropdown,
    printOrder,
    createShipmentFromOrder,
    saving,
  };
}
